import axios from 'axios';
import { reactive, readonly } from 'vue';

const hasWindow = typeof window !== 'undefined';
const hasNavigator = typeof navigator !== 'undefined';

const pwaState = reactive({
  appVersion: process.env.VUE_APP_VERSION || '',
  isOnline: hasNavigator ? navigator.onLine : true,
  isStandalone: false,
  updateAvailable: false,
});

let pendingRegistration = null;
let statusInitialized = false;
let reloadRequested = false;
let updateCheckStarted = false;
let autoUpdateStarted = false;

const PWA_UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Versión nueva del front: no se avisa, se recarga sola en un momento seguro:
// - al navegar a otra pantalla (se carga la pantalla de destino ya actualizada);
// - al ocultarse la pestaña (el usuario no lo ve);
// - tras un rato sin tocar nada, sin un campo de texto activo.
// Nunca con una petición de guardado en curso ni mientras una pantalla lo pida
// (p. ej. el TPV con una mesa abierta o el cliente pagando).
const AUTO_UPDATE_IDLE_MS = 90 * 1000;
const AUTO_UPDATE_POLL_MS = 15 * 1000;
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);
// clave -> minutos sin tocar nada que hacen falta para recargar igualmente
// (Infinity = bloqueo total hasta que la pantalla lo quite).
const updateBlockers = new Map();
let pendingMutations = 0;
let lastInteractionAt = Date.now();

function trackMutatingRequests() {
  axios.interceptors.request.use((config) => {
    if (MUTATING_METHODS.has(String(config?.method || 'get').toLowerCase())) {
      config.__ewuPwaMutation = true;
      pendingMutations += 1;
    }
    return config;
  });
  const settle = (config) => {
    if (config?.__ewuPwaMutation) {
      config.__ewuPwaMutation = false;
      pendingMutations = Math.max(0, pendingMutations - 1);
    }
  };
  axios.interceptors.response.use(
    (response) => { settle(response?.config); return response; },
    (error) => { settle(error?.config); return Promise.reject(error); },
  );
}
trackMutatingRequests();

function getStandaloneStatus() {
  if (!hasWindow || !hasNavigator) return false;

  return window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    navigator.standalone === true;
}

function refreshStandaloneStatus() {
  pwaState.isStandalone = getStandaloneStatus();
}

async function safeUpdateRegistration(registration) {
  if (!registration || typeof registration.update !== 'function') return;

  try {
    await registration.update();
  } catch (error) {
    console.warn('EWU PWA update check failed:', error);
  }
}

function startPwaUpdateChecks() {
  if (!hasWindow || updateCheckStarted) return;

  updateCheckStarted = true;

  const runCheck = () => {
    void safeUpdateRegistration(pendingRegistration);
  };

  window.addEventListener('focus', runCheck);
  window.addEventListener('online', runCheck);
  window.setInterval(runCheck, PWA_UPDATE_CHECK_INTERVAL_MS);
}

function isEditingText() {
  const element = typeof document !== 'undefined' ? document.activeElement : null;
  if (!element) return false;
  return element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);
}

function isOffline() {
  return hasNavigator && navigator.onLine === false;
}

function requiredIdleForBlockers() {
  let required = 0;
  updateBlockers.forEach((minIdleMs) => { required = Math.max(required, minIdleMs); });
  return required;
}

function canReloadAutomatically({ hidden = false } = {}) {
  if (!pwaState.updateAvailable || reloadRequested) return false;
  if (pendingMutations > 0 || isOffline()) return false;
  const idleFor = Date.now() - lastInteractionAt;
  // Pestaña oculta: nadie lo ve, basta con respetar los bloqueos de pantalla.
  if (hidden) return idleFor >= requiredIdleForBlockers();
  if (isEditingText()) return false;
  return idleFor >= Math.max(AUTO_UPDATE_IDLE_MS, requiredIdleForBlockers());
}

function startAutoUpdate() {
  if (!hasWindow || autoUpdateStarted) return;
  autoUpdateStarted = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && canReloadAutomatically({ hidden: true })) {
      applyPwaUpdate();
    }
  });
  window.setInterval(() => {
    if (canReloadAutomatically({ hidden: document.visibilityState === 'hidden' })) {
      applyPwaUpdate();
    }
  }, AUTO_UPDATE_POLL_MS);
}

export function initPwaStatus() {
  if (!hasWindow) return;
  if (statusInitialized) return;

  statusInitialized = true;
  refreshStandaloneStatus();

  window.addEventListener('online', () => {
    pwaState.isOnline = true;
  });

  window.addEventListener('offline', () => {
    pwaState.isOnline = false;
  });

  const noteInteraction = () => { lastInteractionAt = Date.now(); };
  ['pointerdown', 'keydown', 'touchstart', 'wheel'].forEach((eventName) => {
    window.addEventListener(eventName, noteInteraction, { capture: true, passive: true });
  });

  const standaloneQuery = window.matchMedia('(display-mode: standalone)');
  if (standaloneQuery.addEventListener) {
    standaloneQuery.addEventListener('change', refreshStandaloneStatus);
  } else if (standaloneQuery.addListener) {
    standaloneQuery.addListener(refreshStandaloneStatus);
  }

  startPwaUpdateChecks();
}

export function markPwaUpdateAvailable(registration) {
  pendingRegistration = registration;
  pwaState.updateAvailable = true;
  startPwaUpdateChecks();
  startAutoUpdate();
  if (hasWindow && document.visibilityState === 'hidden' && canReloadAutomatically({ hidden: true })) {
    applyPwaUpdate();
  }
}

// Una pantalla puede frenar la recarga automática mientras tanto:
// - minIdleMs = Infinity (por defecto): no recargar hasta quitar el bloqueo
//   (cliente pagando, cobro abierto en el TPV...).
// - minIdleMs = N: solo recargar si nadie toca el dispositivo en N ms
//   (p. ej. el TPV con una mesa abierta).
// La navegación a otra pantalla sigue cargando la versión nueva.
export function setPwaUpdateBlocker(key, blocked, minIdleMs = Infinity) {
  if (blocked) updateBlockers.set(key, minIdleMs);
  else updateBlockers.delete(key);
}

// Carga la versión nueva: la URL de destino si se está navegando o la actual.
export function applyPwaUpdate(targetUrl = null) {
  if (!hasWindow || reloadRequested) return;
  reloadRequested = true;

  let loaded = false;
  const load = () => {
    if (loaded) return;
    loaded = true;
    if (targetUrl) window.location.assign(targetUrl);
    else window.location.reload();
  };

  if (!pendingRegistration || !pendingRegistration.waiting) {
    load();
    return;
  }

  navigator.serviceWorker.addEventListener('controllerchange', load, { once: true });
  pendingRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
  // Por si el service worker nuevo no llega a tomar el control.
  window.setTimeout(load, 4000);
}

// Para el guard global del router: cambiar de pantalla con una versión nueva
// pendiente carga la página entera. No en la primera navegación, ni si solo
// cambian los filtros de la URL, ni con un guardado en curso.
export function shouldReloadOnNavigation(from, to) {
  return pwaState.updateAvailable && !reloadRequested && pendingMutations === 0
    && !isOffline() && Boolean(from?.matched?.length) && Boolean(to?.path)
    && to.path !== from.path;
}

export async function requestPwaUpdateCheck(registration = null) {
  // Guardamos el registro para que las comprobaciones periódicas (foco, online
  // y cada 5 min) funcionen desde el arranque, no solo tras una primera versión.
  if (registration) pendingRegistration = registration;
  startPwaUpdateChecks();
  await safeUpdateRegistration(pendingRegistration);
}

export function getPwaInfo() {
  refreshStandaloneStatus();

  return {
    appVersion: pwaState.appVersion,
    isOnline: pwaState.isOnline,
    isStandalone: pwaState.isStandalone,
  };
}

export const pwaStatus = readonly(pwaState);
